import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';

const { VITE_ANALYTICS_KEY } = import.meta.env;

type UserTrackingOptions = Record<string, string | number | boolean | string[]>;

/** Web vs Mac client — `mac` only when the shell signals it (same as existing station/macos flows). */
function getWebAnalyticsPlatform(): 'web' | 'mac' {
  if (typeof window === 'undefined') {
    return 'web';
  }
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get('platform') === 'macos') {
    return 'mac';
  }
  return 'web';
}

function applySessionUserProperties(): void {
  const identify = new Identify();
  identify.set('platform', getWebAnalyticsPlatform());
  identify.set('app_name', 'napster');
  amplitude.identify(identify);
}

class Analytics {
  private static instance: Analytics;
  private user: string | null = null;

  private constructor() {
    if (window.location.hostname === 'localhost' || !VITE_ANALYTICS_KEY) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const platformParam = searchParams.get('platform');
    const isMacOS = platformParam === 'macos';
    const storedStationMode = localStorage.getItem('napster_station_mode');
    const isStationMode =
      searchParams.get('mode') === 'ces' || searchParams.get('mode') === 'station' || storedStationMode === 'true';

    if (window?.Cookiebot?.consent?.statistics || isMacOS || isStationMode) {
      this.initAnalytics();
    } else {
      window.addEventListener('CookiebotOnAccept', this.initAnalytics, false);
    }
  }

  private initAnalytics(): void {
    window.removeEventListener('CookiebotOnAccept', this.initAnalytics, false);

    amplitude.init(VITE_ANALYTICS_KEY as string, undefined, {
      autocapture: {
        pageViews: true,
        networkTracking: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: true,
        attribution: true,
      },
      defaultTracking: {
        pageViews: true,
      },
      trackingOptions: {
        ipAddress: true,
        language: true,
        platform: true,
      },
    });

    // User properties (apply to all events) — single Amplitude project across Napster clients
    applySessionUserProperties();

    window.addEventListener(
      'CookiebotOnDecline',
      () => {
        amplitude.setOptOut(true);
      },
      false,
    );

    window.addEventListener(
      'CookiebotOnAccept',
      () => {
        amplitude.setOptOut(false);
      },
      false,
    );
  }

  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  public setUser(id: string, options?: UserTrackingOptions): void {
    if (!id || this.user) return;
    this.user = id;
    amplitude.setUserId(id);
    if (options) {
      const identifyObj = new Identify();
      Object.keys(options).forEach((key) => {
        identifyObj.set(key, options[key]);
      });
      amplitude.identify(identifyObj);
    }
  }

  public logEvent(
    eventName: string,
    eventProperties?: Record<string, string | number | boolean>,
  ): void {
    console.log('Tracking event:', eventName, eventProperties);
    amplitude.track(eventName, eventProperties);
  }
}
export const analytics = Analytics.getInstance();
